import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays } from "lucide-react";

export const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

export function buildPresets() {
  const now = new Date();
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return [
    { key: "today", label: "Today", range: () => [iso(now), iso(now)] },
    { key: "yesterday", label: "Yesterday", range: () => { const y = addDays(now, -1); return [iso(y), iso(y)]; } },
    { key: "7d", label: "Last 7 days", range: () => [iso(addDays(now, -6)), iso(now)] },
    { key: "30d", label: "Last 30 days", range: () => [iso(addDays(now, -29)), iso(now)] },
    { key: "this_month", label: "This month", range: () => [iso(startOfMonth(now)), iso(now)] },
    { key: "last_month", label: "Last month", range: () => [iso(startOfMonth(lastMonthDate)), iso(endOfMonth(lastMonthDate))] },
  ];
}

export const fmtDate = (s) => new Date(s + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export function DateRangePicker({ from, to, onChange, theme = "light", testId = "range" }) {
  const dark = theme === "dark";
  const presets = useMemo(buildPresets, []);
  const [preset, setPreset] = useState("custom");
  const [open, setOpen] = useState(false);
  const [tmpFrom, setTmpFrom] = useState(from);
  const [tmpTo, setTmpTo] = useState(to);

  useEffect(() => { setTmpFrom(from); setTmpTo(to); }, [from, to]);

  const applyPreset = (p) => { const [f, t] = p.range(); setPreset(p.key); setOpen(false); onChange(f, t); };
  const applyCustom = () => { if (!tmpFrom || !tmpTo || tmpFrom > tmpTo) return; setPreset("custom"); setOpen(false); onChange(tmpFrom, tmpTo); };

  const activeLabel = preset === "custom"
    ? `${fmtDate(from)} – ${fmtDate(to)}`
    : (presets.find((p) => p.key === preset)?.label || `${fmtDate(from)} – ${fmtDate(to)}`);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid={`${testId}-trigger`}
          className={dark ? "bg-[#0A0A0A] border-zinc-700 text-white hover:bg-zinc-900 hover:text-white gap-2" : "gap-2"}>
          <CalendarDays className="w-4 h-4" />
          {activeLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 flex" data-testid={`${testId}-popover`}>
        <div className="flex flex-col p-2 border-r min-w-[150px]">
          {presets.map((p) => (
            <button key={p.key} data-testid={`${testId}-preset-${p.key}`} onClick={() => applyPreset(p)}
              className={`text-left text-sm px-3 py-2 rounded-md transition-colors ${preset === p.key ? "bg-emerald-500 text-white" : "hover:bg-slate-100"}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="p-3 flex flex-col gap-2">
          <div>
            <label className="text-xs text-slate-500 block mb-1">From</label>
            <Input type="date" data-testid={`${testId}-from`} value={tmpFrom} max={tmpTo} onChange={(e) => setTmpFrom(e.target.value)} className="w-44" />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">To</label>
            <Input type="date" data-testid={`${testId}-to`} value={tmpTo} min={tmpFrom} max={iso(new Date())} onChange={(e) => setTmpTo(e.target.value)} className="w-44" />
          </div>
          <Button size="sm" onClick={applyCustom} data-testid={`${testId}-apply`} className="bg-emerald-600 hover:bg-emerald-700 text-white mt-1">Apply</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
